import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Drawer from '~/components/Drawer';
import MedicalExpenseForm from './MedicalExpenseForm';
import TravelExpenseForm from './TravelExpenseForm';
import FoodExpenseForm from './FoodExpenseForm';
import FlagIssueModal from './FlagIssueModal';
import SubmitForm from './SubmitForm';
import ClearFlagConfirmationModal from '../../modals/DeleteExpenseConfirmationModal';
import { FundRequestExpenseTypes } from '~/enums/fundRequest';
import { ExpenseItem, DefaultExpenseItem } from '~/types/fundrequests';

import { FaFlag } from 'react-icons/fa6';
import { SelectOption } from '~/types/globals';
import { inputStyles } from '~/components/Vendors/styles';
import { useFundRequestContext } from '~/context/FundRequestProvider';

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    categories: SelectOption[];
    glCodes: SelectOption[];
    selectedExpenseCategory: FundRequestExpenseTypes | undefined;
    onSubmit: (data: DefaultExpenseItem | Record<string, unknown>) => Promise<void>;
    handleFlagItem: (
        expenseItem: ExpenseItem | undefined,
        type: FundRequestExpenseTypes | undefined,
        comment: string
    ) => void;
    handleDeleteFlag: (
        expenseItem: ExpenseItem | undefined,
        type: FundRequestExpenseTypes | undefined
    ) => void;
    handleDeleteExpenseItem: (
        expenseItem: ExpenseItem | undefined,
        type: FundRequestExpenseTypes | undefined
    ) => void;
    handleSelectCategory: (category: SelectOption | undefined) => void;
    onDocumentClick?: (slideIndex: number) => void;
    selectedDocumentIndex?: number;
    saving: boolean;
}

// Type guard for checking flagged_comment and flagged_resolved_date properties
const hasFlagProperties = (
    item: unknown
): item is {
    id: number;
    flagged_comment: string | null;
    flagged_resolved_date: string | null;
} => {
    return (
        !!item &&
        typeof item === 'object' &&
        'id' in item &&
        'flagged_comment' in item &&
        'flagged_resolved_date' in item
    );
};

interface FlagItemProps {
    item: ExpenseItem | undefined;
    deleteFlag: () => void;
    deleteExpenseItem: () => void;
    isDeleting?: boolean;
}

const FlagItem = ({ item, deleteFlag, deleteExpenseItem, isDeleting = false }: FlagItemProps) => {
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    if (
        !hasFlagProperties(item) ||
        !item.flagged_comment ||
        item.flagged_resolved_date
    ) {
        return null;
    }

    const handleDeleteClick = () => {
        setShowDeleteConfirmation(true);
    };

    const handleConfirmDelete = () => {
        setShowDeleteConfirmation(false);
        deleteExpenseItem();
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirmation(false);
    };

    return (
        <>
            <div
                key={item.id}
                className='mt-5 rounded-2xl border-2 border-[#F02D3A] bg-[#FDEAEB] p-4'
            >
                <div className='flex items-start space-x-3 mb-4'>
                    <FaFlag className='flex-shrink-0 text-[#F02D3A]' size={16} />
                    <p className='text-sm text-gray-800 leading-relaxed'>{item.flagged_comment}</p>
                </div>
                
                <div className='flex space-x-2'>
                    <button
                        type='button'
                        onClick={deleteFlag}
                        disabled={isDeleting}
                        className='flex items-center space-x-1 rounded-md px-3 py-2 text-xs font-medium text-[#4AAAA5] hover:bg-[#4AAAA5] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        <span>✓</span>
                        <span>APPROVE</span>
                    </button>
                    
                    <button
                        type='button'
                        onClick={handleDeleteClick}
                        disabled={isDeleting}
                        className='flex items-center space-x-1 rounded-md px-3 py-2 text-xs font-medium text-[#F02D3A] hover:bg-[#F02D3A] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        <span>🗑</span>
                        <span>{isDeleting ? 'CLEARING...' : 'DELETE'}</span>
                    </button>
                </div>
            </div>

            <ClearFlagConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
            />
        </>
    );
};

const ExpenseInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    categories,
    selectedExpenseCategory,
    onSubmit,
    handleFlagItem,
    handleDeleteFlag,
    handleDeleteExpenseItem,
    handleSelectCategory,
    onDocumentClick,
    selectedDocumentIndex,
    saving,
}) => {
    const { formKey, fundRequestDispatch } = useFundRequestContext();
    const { handleSubmit, formState, getValues } = useFormContext();
    const { isSubmitting } = formState;
    const [modalOpen, setModalOpen] = useState(false);

    const selectedExpenseItem = formKey ? getValues(formKey) : null;

    useEffect(() => {
        if (!isOpen) {
            setModalOpen(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedExpenseItem?.id && selectedExpenseCategory) {
            fundRequestDispatch({
                type: 'set_upload_path',
                payload: {
                    expenseItemType: selectedExpenseCategory.toLowerCase(),
                    expenseItemId: selectedExpenseItem.id,
                },
            });
        }
    }, [selectedExpenseItem?.id, selectedExpenseCategory, fundRequestDispatch]);

    // Clear upload path when unmounting
    useEffect(() => {
        return () => {
            fundRequestDispatch({
                type: 'reset_upload_path',
                payload: null,
            });
        };
    }, [fundRequestDispatch]);

    // Handle closing the drawer when clicking Cancel
    const handleCancel = () => {
        // Clear pending uploads for this form key when canceling
        if (formKey) {
            fundRequestDispatch({
                type: 'clear_pending_uploads',
                payload: formKey,
            });
        }
        setIsOpen(false);
    };

    // Handle form submission
    const handleFormSubmit = async (data: DefaultExpenseItem | Record<string, unknown>) => {
        try {
            // Don't clear uploads here - let them be processed in handleSave
            await onSubmit(data);
            // After successful save, the drawer will be closed by onSubmit
        } catch (error) {
            console.error('Error submitting form:', error);
            // Don't close drawer on error
        }
    };

    const getExpenseForm = () => {
        switch (selectedExpenseCategory) {
            case FundRequestExpenseTypes.DEFAULT:
                return <MedicalExpenseForm categories={categories} onDocumentClick={onDocumentClick} selectedDocumentIndex={selectedDocumentIndex} />;
            case FundRequestExpenseTypes.TRAVEL:
                return <TravelExpenseForm onDocumentClick={onDocumentClick} selectedDocumentIndex={selectedDocumentIndex} />;
            case FundRequestExpenseTypes.FOOD:
                return <FoodExpenseForm onDocumentClick={onDocumentClick} selectedDocumentIndex={selectedDocumentIndex} />;
            default:
                return <></>;
        }
    };

    const getFlagItems = () => {
        try {
            switch (selectedExpenseCategory) {
                case FundRequestExpenseTypes.DEFAULT:
                    return (
                        <>
                            {selectedExpenseItem && (
                                <FlagItem
                                    item={selectedExpenseItem}
                                    deleteFlag={() =>
                                        handleDeleteFlag(
                                            selectedExpenseItem,
                                            selectedExpenseCategory
                                        )
                                    }
                                    deleteExpenseItem={() =>
                                        handleDeleteExpenseItem(
                                            selectedExpenseItem,
                                            selectedExpenseCategory
                                        )
                                    }
                                    isDeleting={saving}
                                />
                            )}
                        </>
                    );
                case FundRequestExpenseTypes.TRAVEL:
                    return (
                        <>
                            {selectedExpenseItem && (
                                <FlagItem
                                    item={selectedExpenseItem}
                                    deleteFlag={() =>
                                        handleDeleteFlag(
                                            selectedExpenseItem,
                                            selectedExpenseCategory
                                        )
                                    }
                                    deleteExpenseItem={() =>
                                        handleDeleteExpenseItem(
                                            selectedExpenseItem,
                                            selectedExpenseCategory
                                        )
                                    }
                                    isDeleting={saving}
                                />
                            )}
                        </>
                    );
                case FundRequestExpenseTypes.FOOD:
                    return (
                        <>
                            {selectedExpenseItem && (
                                <FlagItem
                                    item={selectedExpenseItem}
                                    deleteFlag={() =>
                                        handleDeleteFlag(
                                            selectedExpenseItem,
                                            selectedExpenseCategory
                                        )
                                    }
                                    deleteExpenseItem={() =>
                                        handleDeleteExpenseItem(
                                            selectedExpenseItem,
                                            selectedExpenseCategory
                                        )
                                    }
                                    isDeleting={saving}
                                />
                            )}
                        </>
                    );
                default:
                    return <></>;
            }
        } catch (error) {
            console.error(error);
        }
    };

    const drawerTitle = !selectedExpenseItem?.id
        ? `Add Expense`
        : `Edit Expense`;

    // Move 'other' category to the bottom of the list of categories
    const categoriesOptions = categories.filter((x) => x.value !== 'other');
    const otherCategory = categories.find((x) => x.value === 'other');
    if (otherCategory) {
        categoriesOptions.push(otherCategory);
    }

    // Create a wrapper for setIsOpen that we'll use just for the Drawer component
    // This ensures the drawer can still be closed via X button without clearing uploads
    const handleDrawerClose = () => {
        setIsOpen(false);
    };

    return (
        <>
            <Drawer
                isOpen={isOpen}
                setIsOpen={handleDrawerClose}
                title={drawerTitle}
                width='w-[500px]'
            >
                {!formKey ? (
                    <>
                        <label
                            htmlFor='mid-primary-medical-reason'
                            className='block font-bold'
                        >
                            Select Category
                        </label>
                        <select
                            className={`${inputStyles} bg-grey w-full`}
                            onChange={(e) => {
                                const category = categories.find(
                                    (c) => c.id === parseInt(e.target.value)
                                );
                                handleSelectCategory(category);
                            }}
                        >
                            <option value=''>Select Category</option>
                            {categoriesOptions.map((category) => (
                                <option
                                    key={category.value}
                                    value={category.id}
                                >
                                    {category.label}
                                </option>
                            ))}
                        </select>
                    </>
                ) : (
                    <form onSubmit={handleSubmit(handleFormSubmit)}>
                        {getExpenseForm()}

                        {getFlagItems()}

                        <SubmitForm
                            isSubmitting={isSubmitting}
                            setIsOpen={handleCancel}
                            disableFlag={!selectedExpenseItem?.id}
                            setModalOpen={() => setModalOpen(!modalOpen)}
                        />
                    </form>
                )}
            </Drawer>

            <FlagIssueModal
                modalOpen={modalOpen}
                setModalOpen={() => setModalOpen(!modalOpen)}
                selectedExpenseCategory={selectedExpenseCategory}
                selectedExpenseItem={selectedExpenseItem}
                handleFlagItem={handleFlagItem}
                saving={saving}
            />
        </>
    );
};

export default ExpenseInfoDrawer;
